import { NavLink } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconDefinition } from "@fortawesome/free-solid-svg-icons";
import { cn } from "@/utils/cn";

export type NavItemType = { path: string; icon: IconDefinition; label: string; level: 1 | 2 };

type NavItemVariant = "desktop" | "mobile-bar" | "slideout";

interface NavItemProps {
  to: string;
  icon: IconDefinition;
  label: string;
  variant?: NavItemVariant;
  onClick?: () => void;
  hideLabel?: boolean;
}

const variantStyles: Record<NavItemVariant, { base: string; active: string; inactive: string; iconSize: string }> = {
  desktop: {
    base: "flex items-center px-4 py-2 rounded-lg transition-all duration-200",
    active: "bg-primary_button/20 dark:bg-primary_button/15 text-primary_button",
    inactive: "text-light/80 dark:text-dark/80 hover:bg-light-muted/80 dark:hover:bg-dark-surface/60",
    iconSize: "w-4 h-4",
  },
  "mobile-bar": {
    base: "flex flex-col items-center space-y-1 px-2 py-2 rounded-lg transition-all duration-200",
    active: "text-primary_button",
    inactive: "text-light/80 dark:text-dark/70",
    iconSize: "w-5 h-5",
  },
  slideout: {
    base: "flex items-center space-x-3 px-4 py-3 rounded-lg transition-all duration-200",
    active: "bg-primary_button/20 dark:bg-primary_button/15 text-primary_button",
    inactive: "text-light/90 dark:text-dark/80 hover:bg-light-muted/60 dark:hover:bg-dark-surface/40",
    iconSize: "w-5 h-5",
  },
};

export function NavItem({ to, icon, label, variant = "desktop", onClick, hideLabel = false }: NavItemProps) {
  const styles = variantStyles[variant];
  const shouldHideLabel = hideLabel && variant === "desktop";

  return (
    <NavLink
      to={to}
      onClick={onClick}
      aria-label={shouldHideLabel ? label : undefined}
      title={shouldHideLabel ? label : undefined}
      className={({ isActive }) =>
        cn(styles.base, !shouldHideLabel && "space-x-2", isActive ? styles.active : styles.inactive)
      }
    >
      <FontAwesomeIcon icon={icon} className={styles.iconSize} aria-hidden="true" />
      {!shouldHideLabel && (
        <span className={variant === "mobile-bar" ? "text-xs font-medium" : "font-medium"}>
          {label}
        </span>
      )}
    </NavLink>
  );
}

